import type { EmergencyAccessSessionDetail } from '@usacsmideo/types';

/**
 * Pure grouping / filtering / recency helpers for the Sessions Surface
 * (CU-86aj1c9ev). Kept rendering-free so the recency-window boundary, the
 * day-grouping, and the auto-expand rule are unit-testable in isolation.
 */

/**
 * The recency auto-expand window: a family-member group auto-expands if it has
 * a session newer than this (convergence redesign §2.2). 15 minutes — the SAME
 * value as the `emergencyAccessSessions` live-session window (CU-86ahxautv),
 * reused deliberately rather than inventing a new constant: "a group is open if
 * it has activity within one live-session window."
 */
export const RECENCY_AUTOEXPAND_MS = 15 * 60 * 1000;

export type RecencyFilter = '24h' | 'week' | 'month' | 'all';

const RECENCY_FILTER_MS: Record<Exclude<RecencyFilter, 'all'>, number> = {
  '24h': 24 * 60 * 60 * 1000,
  week: 7 * 24 * 60 * 60 * 1000,
  month: 30 * 24 * 60 * 60 * 1000,
};

/** Filter sessions to those within the chosen recency window (relative to now). */
export function filterByRecency(
  sessions: EmergencyAccessSessionDetail[],
  filter: RecencyFilter,
  now: number
): EmergencyAccessSessionDetail[] {
  if (filter === 'all') return sessions;
  const cutoff = now - RECENCY_FILTER_MS[filter];
  return sessions.filter((s) => s.createdAt >= cutoff);
}

/**
 * True if a group should auto-expand: it has a session created within the
 * recency window (§2.2). A `?focus=`/path-param override is applied by the
 * caller and always wins over this.
 */
export function shouldAutoExpand(
  sessions: EmergencyAccessSessionDetail[],
  now: number
): boolean {
  const cutoff = now - RECENCY_AUTOEXPAND_MS;
  return sessions.some((s) => s.createdAt >= cutoff);
}

export interface DayGroup {
  /** Local-day key (YYYY-MM-DD) for the cluster header. */
  dayKey: string;
  /** Epoch ms of local midnight for that day — for header label formatting. */
  dayStart: number;
  sessions: EmergencyAccessSessionDetail[];
}

/** Local-day key for an epoch-ms timestamp (YYYY-MM-DD, viewer's timezone). */
function dayKeyOf(ms: number): string {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dayStartOf(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Cluster sessions into day groups, newest-day-first, sessions newest-first
 * within each day (Timeline Filter Rules §D3). Grouping is visual only — every
 * session remains its own card; no merge, no hidden counts. Assumes input is
 * already the (bot-excluded) list from the resolver.
 */
export function groupByDay(
  sessions: EmergencyAccessSessionDetail[]
): DayGroup[] {
  const byKey = new Map<string, DayGroup>();

  for (const s of sessions) {
    const key = dayKeyOf(s.createdAt);
    let group = byKey.get(key);
    if (!group) {
      group = { dayKey: key, dayStart: dayStartOf(s.createdAt), sessions: [] };
      byKey.set(key, group);
    }
    group.sessions.push(s);
  }

  const groups = Array.from(byKey.values());
  groups.sort((a, b) => b.dayStart - a.dayStart); // newest day first
  for (const g of groups) g.sessions.sort((a, b) => b.createdAt - a.createdAt);
  return groups;
}
